import { FC, useEffect, useState, useMemo, useContext } from "react";
import { Table, Checkbox, Select, Modal } from "antd";
import { ColumnsType } from "antd/es/table";
import { useModalAction } from "../../components/modal-views/context";
import { PlusIcon } from "src/components/icons/plus-icon";
import { EditIcon } from "src/components/icons/edit-icon";
import { SettingsIcon } from "src/components/icons/settings-icon";
import { DeleteIcon } from "src/components/icons/delete-icon";
import { BackIcon } from "src/components/icons/back-icon";
import { ResetIcon } from "src/components/icons/reset-icon";
import { CopyIcon } from "src/components/icons/copy-icon";
import { SearchIcon } from "src/components/icons/search-icon";
import { CloseIcon } from "src/components/icons/close-icon";
import Button from "src/components/ui/button";
import ToggleSwitch from "src/components/ui/switch";
import SearchFilter_1 from "src/components/search-filters/user-management-filter_1";
import client from "src/api";
import {
  RoleApiItem,
  RolePermissionDetailItem,
  SubPermissionsObj,
  ActiveChannelPartnerItem,
} from "src/api/types";
import GlobalContext from "src/context/global-context";
import { capitalizeFirstLetter, getErrorMessage } from "src/lib/utils";
import {
  APISomethingWrongMsg,
  NetworkDisconnectMsg,
  LIST_PAGE_SIZE,
  permissions as permissionConstants,
} from "src/lib/constants";
import usePermissions from "src/hooks/use-permissions";
import PermissionGate from "src/components/shared/permission-gate";

const ManageRolesPermissionPage: FC = () => {
  const { openModal } = useModalAction();
  const { setAlertText, setIsAlertOpened } = useContext(GlobalContext);

  const { canView, canList, canAdd, canEdit, canDelete } = usePermissions([permissionConstants.ROLES_PERMISSIONS]);
  const canViewRoles = canView || canList;

  // Roles Table State
  const [roles, setRoles] = useState<RoleApiItem[]>([]);
  const [loadingRoles, setLoadingRoles] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(LIST_PAGE_SIZE);
  const [totalRoles, setTotalRoles] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Manage Permissions State
  const [isManagingPermissions, setIsManagingPermissions] = useState(false);
  const [selectedRole, setSelectedRole] = useState<{ id: number; name: string } | null>(null);
  const [rolePermissions, setRolePermissions] = useState<RolePermissionDetailItem[]>([]);
  const [activePermissionId, setActivePermissionId] = useState<number | null>(null);
  const [loadingPermissions, setLoadingPermissions] = useState(false);
  const [permissionFilterQuery, setPermissionFilterQuery] = useState("");
  const [submittingPermissions, setSubmittingPermissions] = useState(false);

  // Active Channel Partners State
  const [channelPartners, setChannelPartners] = useState<ActiveChannelPartnerItem[]>([]);
  const [loadingChannelPartners, setLoadingChannelPartners] = useState(false);
  const [selectedChannelPartnerId, setSelectedChannelPartnerId] = useState<number | null>(null);

  // Create / Edit Role Modal State
  const [isRoleModalOpen, setIsRoleModalOpen] = useState(false);
  const [editingRole, setEditingRole] = useState<RoleApiItem | null>(null);
  const [newRoleName, setNewRoleName] = useState("");
  const [createRoleLoading, setCreateRoleLoading] = useState(false);

  // Duplicate Role Modal State
  const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState(false);
  const [duplicatingRole, setDuplicatingRole] = useState<RoleApiItem | null>(null);
  const [duplicateRoleName, setDuplicateRoleName] = useState("");
  const [duplicateChannelPartnerId, setDuplicateChannelPartnerId] = useState<number | null>(null);
  const [duplicateLoading, setDuplicateLoading] = useState(false);

  // Status Toggle Loading State
  const [updatingRoleId, setUpdatingRoleId] = useState<number | null>(null);

  // Debounce search input and reset page to 1
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchQuery.trim());
      setCurrentPage(1);
    }, 500);

    return () => clearTimeout(handler);
  }, [searchQuery]);

  useEffect(() => {
    if (!canViewRoles) return;
    fetchActiveChannelPartners();
  }, [canViewRoles]);

  const fetchActiveChannelPartners = async () => {
    setLoadingChannelPartners(true);
    try {
      const list = await client.permissions.getActiveChannelPartners();
      setChannelPartners(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error("Failed to load active channel partners", err);
      setChannelPartners([]);
    } finally {
      setLoadingChannelPartners(false);
    }
  };

  const fetchRoles = async (page: number, limit: number, search?: string) => {
    setLoadingRoles(true);
    try {
      const res = await client.permissions.getRoles(page, limit, search, "name");
      const safeData = Array.isArray(res.data) ? res.data : [];
      setRoles(safeData);
      setTotalRoles(res.total || safeData.length);
    } catch (err: any) {
      setRoles([]);
      setTotalRoles(0);
      const errMsg = getErrorMessage(err?.response?.data || err?.data || err);
      if (err?.code === "ERR_NETWORK") {
        setAlertText(NetworkDisconnectMsg);
      } else {
        setAlertText(errMsg);
      }
      setIsAlertOpened(true);
    } finally {
      setLoadingRoles(false);
    }
  };

  useEffect(() => {
    if (!canViewRoles) return;
    fetchRoles(currentPage, pageSize, debouncedSearch);
  }, [canViewRoles, currentPage, pageSize, debouncedSearch]);

  const onPageChange = (page: number, newPageSize: number) => {
    setCurrentPage(page);
    setPageSize(newPageSize);
  };

  const handleOpenManagePermissions = async (role: RoleApiItem) => {
    setSelectedRole({ id: role.id, name: role.name });
    setIsManagingPermissions(true);
    setLoadingPermissions(true);
    setPermissionFilterQuery("");
    setActivePermissionId(null);

    try {
      const details = await client.permissions.getRolePermissionsDetails(role.id);
      const fetchedList: RolePermissionDetailItem[] = details?.permissions || [];
      const normalizedList = fetchedList.map((item) => {
        const isAssigned = Boolean(item.assigned || isAnySubChecked(item.subPermissions));
        return {
          ...item,
          assigned: isAssigned,
          subPermissions: isAssigned
            ? {
              ...(item.subPermissions || ALL_SUB_FALSE),
              can_list: true,
            }
            : { ...(item.subPermissions || ALL_SUB_FALSE), can_list: false },
        };
      });
      setRolePermissions(normalizedList);
      if (normalizedList.length > 0) {
        setActivePermissionId(normalizedList[0].id);
      }
    } catch (err: any) {
      if (err.code === "ERR_NETWORK") {
        setAlertText(NetworkDisconnectMsg);
      } else if (err.response?.data) {
        setAlertText(getErrorMessage(err.response.data));
      } else {
        setAlertText(APISomethingWrongMsg);
      }
      setIsAlertOpened(true);
      setRolePermissions([]);
    } finally {
      setLoadingPermissions(false);
    }
  };

  const handleOpenCreateRole = () => {
    if (!canAdd) {
      setAlertText("You do not have permission to create roles");
      setIsAlertOpened(true);
      return;
    }
    setEditingRole(null);
    setNewRoleName("");
    setSelectedChannelPartnerId(null);
    setIsRoleModalOpen(true);
    if (channelPartners.length === 0) {
      fetchActiveChannelPartners();
    }
  };

  const handleOpenEditRole = async (role: RoleApiItem) => {
    if (!canEdit) {
      setAlertText("You do not have permission to edit roles");
      setIsAlertOpened(true);
      return;
    }
    setEditingRole(role);
    setNewRoleName(role.name);
    const cpId =
      role.channelPartner?.id ??
      role.channel_partner?.id ??
      role.channel_partner_id ??
      null;
    setSelectedChannelPartnerId(cpId);
    setIsRoleModalOpen(true);

    try {
      const fetched = await client.permissions.getRoleById(role.id);
      if (fetched?.name) {
        setNewRoleName(fetched.name);
      }
      const fetchedCpId =
        fetched?.channelPartner?.id ??
        fetched?.channel_partner?.id ??
        fetched?.channel_partner_id;
      if (fetchedCpId) {
        setSelectedChannelPartnerId(fetchedCpId);
      }
    } catch (err) {
      // Pre-filled with role.name
    }
  };

  const handleSaveRoleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (editingRole && !canEdit) {
      setAlertText("You do not have permission to edit roles");
      setIsAlertOpened(true);
      return;
    }

    if (!editingRole && !canAdd) {
      setAlertText("You do not have permission to create roles");
      setIsAlertOpened(true);
      return;
    }

    if (!editingRole && !selectedChannelPartnerId) {
      setAlertText("error_Please select a channel partner!");
      setIsAlertOpened(true);
      return;
    }

    if (!newRoleName.trim()) {
      setAlertText("error_Please enter role name!");
      setIsAlertOpened(true);
      return;
    }

    setCreateRoleLoading(true);
    try {
      if (editingRole) {
        const res = await client.permissions.updateRole(editingRole.id, newRoleName.trim());
        if (res?.status === "failure" || (res?.code && res.code >= 400)) {
          setAlertText(getErrorMessage(res));
          setIsAlertOpened(true);
          return;
        }
        setAlertText(`Role "${newRoleName.trim()}" updated successfully!`);
        setIsAlertOpened(true);
      } else {
        const res = await client.permissions.createRole({
          channel_partner_id: selectedChannelPartnerId!,
          name: newRoleName.trim(),
        });
        if (res?.status === "failure" || (res?.code && res.code >= 400)) {
          setAlertText(getErrorMessage(res));
          setIsAlertOpened(true);
          return;
        }
        setAlertText(`Role "${newRoleName.trim()}" created successfully!`);
        setIsAlertOpened(true);
      }
      fetchRoles(currentPage, pageSize, debouncedSearch);
      setNewRoleName("");
      setSelectedChannelPartnerId(null);
      setEditingRole(null);
      setIsRoleModalOpen(false);
    } catch (error: any) {
      if (error.code === "ERR_NETWORK") {
        setAlertText(NetworkDisconnectMsg);
      } else if (error.response?.data) {
        setAlertText(getErrorMessage(error.response.data));
      } else {
        setAlertText(APISomethingWrongMsg);
      }
      setIsAlertOpened(true);
    } finally {
      setCreateRoleLoading(false);
    }
  };

  const handleToggleRoleStatus = async (role: RoleApiItem, newStatus: boolean) => {
    if (!canEdit) {
      setAlertText("You do not have permission to update role status");
      setIsAlertOpened(true);
      return;
    }
    if (Boolean(role.is_assigned_admin) && !newStatus) {
      setAlertText("Cannot disable this role because it is already assigned to admins");
      setIsAlertOpened(true);
      return;
    }
    setUpdatingRoleId(role.id);
    try {
      const res = await client.permissions.updateRole(role.id, {
        is_active: newStatus,
      });

      if (res?.status === "failure" || (res?.code && res.code >= 400)) {
        setAlertText(getErrorMessage(res));
        setIsAlertOpened(true);
        return;
      }

      setRoles((prevRoles) =>
        prevRoles.map((r) =>
          r.id === role.id ? { ...r, is_active: newStatus } : r
        )
      );
      setAlertText(`Role "${role.name}" status updated successfully!`);
      setIsAlertOpened(true);
    } catch (error: any) {
      if (error.code === "ERR_NETWORK") {
        setAlertText(NetworkDisconnectMsg);
      } else if (error.response?.data) {
        setAlertText(getErrorMessage(error.response.data));
      } else {
        setAlertText(APISomethingWrongMsg);
      }
      setIsAlertOpened(true);
    } finally {
      setUpdatingRoleId(null);
    }
  };

  const handleOpenDuplicateRole = async (role: RoleApiItem) => {
    if (!canAdd) {
      setAlertText("You do not have permission to duplicate roles");
      setIsAlertOpened(true);
      return;
    }
    setDuplicatingRole(role);
    setDuplicateRoleName(role.name);
    const cpId =
      role.channelPartner?.id ??
      role.channel_partner?.id ??
      role.channel_partner_id ??
      null;
    setDuplicateChannelPartnerId(cpId);
    setIsDuplicateModalOpen(true);

    if (channelPartners.length === 0) {
      fetchActiveChannelPartners();
    }

    try {
      const fetched = await client.permissions.getRoleById(role.id);
      if (fetched?.name) {
        setDuplicateRoleName(fetched.name);
      }
      const fetchedCpId =
        fetched?.channelPartner?.id ??
        fetched?.channel_partner?.id ??
        fetched?.channel_partner_id;
      if (fetchedCpId) {
        setDuplicateChannelPartnerId(fetchedCpId);
      }
    } catch (err) {
      // Pre-filled with role.name / role.channel_partner_id
    }
  };

  const handleDuplicateRoleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!duplicatingRole) return;

    if (!canAdd) {
      setAlertText("You do not have permission to duplicate roles");
      setIsAlertOpened(true);
      return;
    }

    if (!duplicateChannelPartnerId) {
      setAlertText("error_Please select a channel partner!");
      setIsAlertOpened(true);
      return;
    }

    if (!duplicateRoleName.trim()) {
      setAlertText("error_Please enter role name!");
      setIsAlertOpened(true);
      return;
    }

    setDuplicateLoading(true);
    try {
      const res = await client.permissions.duplicateRole({
        channel_partner_id: duplicateChannelPartnerId,
        role_id: duplicatingRole.id,
        roleName: duplicateRoleName.trim(),
      });

      if (res?.status === "failure" || (res?.code && res.code >= 400)) {
        setAlertText(getErrorMessage(res));
        setIsAlertOpened(true);
        return;
      }

      setAlertText(`Role "${duplicateRoleName.trim()}" duplicated successfully!`);
      setIsAlertOpened(true);
      fetchRoles(currentPage, pageSize, debouncedSearch);
      setDuplicateRoleName("");
      setDuplicateChannelPartnerId(null);
      setDuplicatingRole(null);
      setIsDuplicateModalOpen(false);
    } catch (error: any) {
      if (error.code === "ERR_NETWORK") {
        setAlertText(NetworkDisconnectMsg);
      } else if (error.response?.data) {
        setAlertText(getErrorMessage(error.response.data));
      } else {
        setAlertText(APISomethingWrongMsg);
      }
      setIsAlertOpened(true);
    } finally {
      setDuplicateLoading(false);
    }
  };

  const handleDeleteRole = (role: RoleApiItem) => {
    if (!canDelete) {
      setAlertText("You do not have permission to delete roles");
      setIsAlertOpened(true);
      return;
    }
    openModal("GENERAL_DELETE", {
      callback: async () => {
        try {
          await client.permissions.deleteRole(role.id);
          setAlertText(`Role "${role.name}" deleted successfully.`);
          setIsAlertOpened(true);
          fetchRoles(currentPage, pageSize, debouncedSearch);
        } catch (error: any) {
          if (error.code === "ERR_NETWORK") {
            setAlertText(NetworkDisconnectMsg);
          } else if (error.response?.data) {
            setAlertText(getErrorMessage(error.response.data));
          } else {
            setAlertText(APISomethingWrongMsg);
          }
          setIsAlertOpened(true);
        }
      },
      isLoading: () => false,
    });
  };

  const ALL_SUB_TRUE: SubPermissionsObj = {
    can_add: true,
    can_edit: true,
    can_list: true,
    can_view: true,
    can_delete: true,
    can_approve_reject: true,
    can_export: true,
    can_download: true,
    can_block: true,
  };

  const ALL_SUB_FALSE: SubPermissionsObj = {
    can_add: false,
    can_edit: false,
    can_list: false,
    can_view: false,
    can_delete: false,
    can_approve_reject: false,
    can_export: false,
    can_download: false,
    can_block: false,
  };

  const isAnySubChecked = (sub?: SubPermissionsObj): boolean => {
    if (!sub) return false;
    return Boolean(
      sub.can_add ||
      sub.can_edit ||
      sub.can_list ||
      sub.can_view ||
      sub.can_delete ||
      sub.can_approve_reject ||
      sub.can_export ||
      sub.can_download ||
      sub.can_block
    );
  };

  // Toggle main assigned checkbox for a permission item
  const handleToggleAssigned = (id: number, checked: boolean) => {
    if (!canEdit) return;
    setRolePermissions((prev) =>
      prev.map((item) => {
        if (item.id === id) {
          if (checked) {
            return {
              ...item,
              assigned: true,
              subPermissions: {
                ...(item.subPermissions || ALL_SUB_FALSE),
                can_list: true,
              },
            };
          } else {
            return {
              ...item,
              assigned: false,
              subPermissions: { ...ALL_SUB_FALSE },
            };
          }
        }
        return item;
      })
    );
  };

  // Toggle subpermission checkbox for a permission item
  const handleToggleSubPermission = (
    permissionId: number,
    subKey: keyof SubPermissionsObj,
    checked: boolean
  ) => {
    if (!canEdit || subKey === "can_list") {
      return;
    }

    setRolePermissions((prev) =>
      prev.map((item) => {
        if (item.id === permissionId) {
          const updatedSubs: SubPermissionsObj = {
            ...(item.subPermissions || ALL_SUB_FALSE),
            [subKey]: checked,
          };

          // If checking any subpermission or if item was already assigned, ensure can_list is true
          const isAssigned = item.assigned || checked;
          if (isAssigned) {
            updatedSubs.can_list = true;
          }

          return {
            ...item,
            assigned: isAssigned,
            subPermissions: isAssigned ? updatedSubs : { ...ALL_SUB_FALSE },
          };
        }
        return item;
      })
    );
  };

  // Toggle all subpermissions for a permission item
  const handleSelectAllSubForPermission = (permissionId: number, checked: boolean) => {
    if (!canEdit) return;
    setRolePermissions((prev) =>
      prev.map((item) => {
        if (item.id === permissionId) {
          return {
            ...item,
            assigned: checked,
            subPermissions: checked ? { ...ALL_SUB_TRUE } : { ...ALL_SUB_FALSE },
          };
        }
        return item;
      })
    );
  };

  // Global actions for permissions matrix (Assign All / Unassign All)
  const handleSelectAllAssigned = (checked: boolean) => {
    if (!canEdit) return;
    setRolePermissions((prev) =>
      prev.map((item) => ({
        ...item,
        assigned: checked,
        subPermissions: checked ? { ...ALL_SUB_TRUE } : { ...ALL_SUB_FALSE },
      }))
    );
  };

  const handleSavePermissions = async () => {
    if (!canEdit) {
      setAlertText("You do not have permission to save role permissions");
      setIsAlertOpened(true);
      return;
    }
    if (!selectedRole) return;

    const hasAssignedPermission = rolePermissions.some(
      (item) => item.assigned || isAnySubChecked(item.subPermissions)
    );
    if (!hasAssignedPermission) {
      setAlertText("Please assign at least one permission to the role!");
      setIsAlertOpened(true);
      return;
    }

    setSubmittingPermissions(true);

    const payload = {
      role_id: selectedRole.id,
      permissions: rolePermissions.map((item) => {
        const isAssigned = Boolean(item.assigned);
        return {
          id: item.id,
          assigned: isAssigned,
          subPermissions: isAssigned
            ? {
              ...(item.subPermissions || ALL_SUB_FALSE),
              can_list: true,
            }
            : { ...ALL_SUB_FALSE },
        };
      }),
    };

    try {
      const res = await client.permissions.saveRolePermissions(payload);
      if (res?.status === "failure" || (res?.code && res.code >= 400)) {
        setAlertText(getErrorMessage(res));
        setIsAlertOpened(true);
        return;
      }
      setAlertText(`Permissions for role "${selectedRole.name}" updated successfully!`);
      setIsAlertOpened(true);
      setIsManagingPermissions(false);
    } catch (error: any) {
      if (error.code === "ERR_NETWORK") {
        setAlertText(NetworkDisconnectMsg);
      } else if (error.response?.data) {
        setAlertText(getErrorMessage(error.response.data));
      } else {
        setAlertText(APISomethingWrongMsg);
      }
      setIsAlertOpened(true);
    } finally {
      setSubmittingPermissions(false);
    }
  };

  // Active permission for detailed 60% panel
  const activePermission = useMemo(() => {
    if (!rolePermissions || rolePermissions.length === 0) return null;
    const found = rolePermissions.find((p) => p.id === activePermissionId);
    return found || rolePermissions[0] || null;
  }, [rolePermissions, activePermissionId]);

  // Real-time assigned permissions list
  const assignedPermissions = useMemo(() => {
    const list = Array.isArray(rolePermissions) ? rolePermissions : [];
    return list.filter((item) => item.assigned);
  }, [rolePermissions]);

  // Helper to count enabled sub-permissions
  const getGrantedSubCount = (sub?: SubPermissionsObj): number => {
    if (!sub) return 0;
    return (
      (sub.can_list ? 1 : 0) +
      (sub.can_view ? 1 : 0) +
      (sub.can_add ? 1 : 0) +
      (sub.can_edit ? 1 : 0) +
      (sub.can_delete ? 1 : 0) +
      (sub.can_approve_reject ? 1 : 0) +
      (sub.can_export ? 1 : 0) +
      (sub.can_download ? 1 : 0) +
      (sub.can_block ? 1 : 0)
    );
  };

  // Total granted sub-permissions across all assigned modules
  const totalGrantedSubCount = useMemo(() => {
    return assignedPermissions.reduce((acc, curr) => {
      return acc + getGrantedSubCount(curr.subPermissions);
    }, 0);
  }, [assignedPermissions]);

  // Check if all permissions are assigned
  const isAllAssigned = useMemo(() => {
    if (!rolePermissions || rolePermissions.length === 0) return false;
    return rolePermissions.every((item) => item.assigned);
  }, [rolePermissions]);

  // Check if none of the permissions are assigned
  const isNoneAssigned = useMemo(() => {
    if (!rolePermissions || rolePermissions.length === 0) return true;
    return rolePermissions.every((item) => !item.assigned);
  }, [rolePermissions]);

  // Check if all sub-permissions for active permission are checked
  const isAllSubChecked = useMemo(() => {
    if (!activePermission || !activePermission.assigned || !activePermission.subPermissions) return false;
    const subs = activePermission.subPermissions;
    return Boolean(
      subs.can_list &&
      subs.can_view &&
      subs.can_add &&
      subs.can_edit &&
      subs.can_delete &&
      subs.can_approve_reject &&
      subs.can_export &&
      subs.can_download &&
      subs.can_block
    );
  }, [activePermission]);

  // Check if no sub-permissions for active permission are checked
  const isNoneSubChecked = useMemo(() => {
    if (!activePermission || !activePermission.assigned || !activePermission.subPermissions) return true;
    const subs = activePermission.subPermissions;
    return (
      !subs.can_view &&
      !subs.can_add &&
      !subs.can_edit &&
      !subs.can_delete &&
      !subs.can_approve_reject &&
      !subs.can_export &&
      !subs.can_download &&
      !subs.can_block
    );
  }, [activePermission]);

  // Filtered Permissions Matrix Items
  const filteredRolePermissions = useMemo(() => {
    const list = Array.isArray(rolePermissions) ? rolePermissions : [];
    if (!permissionFilterQuery.trim()) return list;
    const q = permissionFilterQuery.toLowerCase().trim();
    return list.filter(
      (item) =>
        item.name?.toLowerCase().includes(q) ||
        item.description?.toLowerCase().includes(q) ||
        String(item.id)?.toLowerCase().includes(q)
    );
  }, [rolePermissions, permissionFilterQuery]);

  // Subpermission keys for UI rendering with descriptions
  const subPermissionKeys: {
    key: keyof SubPermissionsObj;
    label: string;
    desc: string;
  }[] = [
      { key: "can_list", label: "List", desc: "View and browse data records" },
      { key: "can_view", label: "View", desc: "View detailed records and modals" },
      { key: "can_add", label: "Add", desc: "Create and add new records" },
      { key: "can_edit", label: "Edit", desc: "Modify and update existing data" },
      { key: "can_delete", label: "Delete", desc: "Delete and remove records" },
      { key: "can_approve_reject", label: "Approve / Reject", desc: "Approve or reject requests" },
      { key: "can_export", label: "Export", desc: "Export records and reports" },
      { key: "can_download", label: "Download", desc: "Download attachments and files" },
      { key: "can_block", label: "Block", desc: "Block or suspend access" },
    ];

  // Roles Table Columns
  const columns: ColumnsType<RoleApiItem> = [
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          ID
        </p>
      ),
      dataIndex: "id",
      key: "id",
      className: "w-auto sm:w-[100px]",
      render: (value: number) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-primary text-[14px] font-medium">
            {value}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Role Name
        </p>
      ),
      dataIndex: "name",
      key: "name",
      className: "w-auto",
      render: (value: string) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-primary text-[14px] font-medium">
            {capitalizeFirstLetter(value || "")}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Channel Partner
        </p>
      ),
      key: "channel_partner",
      className: "w-auto",
      render: (_, record) => {
        const cpName = record.channelPartner?.name || record.channel_partner?.name;
        return (
          <div className="flex flex-row gap-2 items-center">
            <p className="font-manrope text-primary text-[14px] font-medium">
              {cpName ? capitalizeFirstLetter(cpName) : "-"}
            </p>
          </div>
        );
      },
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Status
        </p>
      ),
      dataIndex: "is_active",
      key: "is_active",
      className: "w-[120px]",
      render: (isActive: boolean | undefined, record: RoleApiItem) => {
        const isAssignedAdmin = Boolean(record.is_assigned_admin);
        const isDisabled = !canEdit || updatingRoleId === record.id || isAssignedAdmin;
        const tooltipText = isAssignedAdmin
          ? "Cannot disable this role because it is already assigned to admins"
          : !canEdit
            ? "Read only - No edit permission"
            : undefined;

        return (
          <div
            className={`flex flex-row gap-2 items-center ${isDisabled ? "opacity-50 cursor-not-allowed" : ""}`}
            title={tooltipText}
            onClick={() => {
              if (isAssignedAdmin) {
                setAlertText("Cannot disable this role because it is already assigned to admins");
                setIsAlertOpened(true);
              }
            }}
          >
            <ToggleSwitch
              type={`${record.id}`}
              value={!!isActive}
              disabled={isDisabled}
              onValueChanged={(_: string, newValue: boolean) => {
                if (!canEdit) return;
                if (isAssignedAdmin && !newValue) {
                  setAlertText("Cannot disable this role because it is already assigned to admins");
                  setIsAlertOpened(true);
                  return;
                }
                handleToggleRoleStatus(record, newValue);
              }}
            />
          </div>
        );
      },
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Permissions
        </p>
      ),
      key: "manage_permissions",
      className: "w-auto sm:w-[150px]",
      render: (_, record) => {
        if (!canEdit && !canView) {
          return (
            <p className="font-manrope text-gray-400 text-[20px] font-bold text-center">
              —
            </p>
          );
        }
        return (
          <div className="flex flex-row gap-2 items-center">
            <button
              className="flex flex-row items-center gap-1.5 px-2.5 py-1 hover:bg-gray rounded-[6px] transition-colors"
              onClick={() => handleOpenManagePermissions(record)}
              title={canEdit ? "Manage Permissions" : "View Permissions"}
            >
              <SettingsIcon className="w-[16px] h-[16px] text-primary" />
            </button>
          </div>
        );
      },
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Action
        </p>
      ),
      key: "actions",
      className: "w-[100px]",
      render: (_, record) => {
        if (!canEdit && !canAdd) {
          return (
            <p className="font-manrope text-gray-400 text-[20px] font-bold text-center">
              —
            </p>
          );
        }
        return (
          <div className="flex flex-row items-center gap-1">
            {canEdit && (
              <button
                className="w-[30px] h-[30px] flex items-center justify-center hover:bg-gray rounded"
                onClick={() => handleOpenEditRole(record)}
                title="Edit Role"
              >
                <EditIcon className="w-[20px] h-[20px] text-primary" />
              </button>
            )}
            {canAdd && (
              <button
                className="w-[30px] h-[30px] flex items-center justify-center hover:bg-gray rounded"
                onClick={() => handleOpenDuplicateRole(record)}
                title="Duplicate Role"
              >
                <CopyIcon className="w-[20px] h-[20px] text-primary" />
              </button>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <div className="w-full h-full bg-gray">
      {isManagingPermissions ? (
        <div className="w-full flex flex-col p-2 sm:p-4">
          {/* Header with Back button, Role Name and Top Actions */}
          <div className="w-full flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 bg-white p-3.5 rounded-[12px] border border-light-300 shadow-sm">
            <div className="flex items-center gap-3">
              <button
                type="button"
                className="w-[38px] h-[38px] flex items-center justify-center bg-gray hover:bg-[#E6E8EC] rounded-[8px] border border-light-300 transition-colors flex-shrink-0"
                onClick={() => setIsManagingPermissions(false)}
                title="Back to Roles"
              >
                <BackIcon className="w-[18px] h-[18px] text-primary" />
              </button>
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-[11px] font-manrope font-semibold text-secondary uppercase tracking-wider">
                    Role Permissions Configuration
                  </span>
                </div>
                <p className="text-[20px] sm:text-[24px] font-manrope text-primary font-bold leading-tight">
                  {selectedRole?.name}
                </p>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                disabled={!canEdit || isAllAssigned}
                title={!canEdit ? "Read only - No edit permission" : undefined}
                onClick={() => handleSelectAllAssigned(true)}
                className={`px-3 py-2 rounded-[6px] font-manrope text-[13px] font-semibold border transition-all shadow-sm ${!canEdit
                  ? "bg-light-100 text-[#9CA3AF] border-light-300 opacity-50 cursor-not-allowed"
                  : isAllAssigned
                    ? "bg-light-100 text-[#9CA3AF] border-light-300 cursor-not-allowed"
                    : isNoneAssigned
                      ? "bg-white text-primary border-border hover:bg-[#E6E8EC] font-bold cursor-pointer"
                      : "bg-white hover:bg-[#E6E8EC] text-primary border-light-300 cursor-pointer"
                  }`}
              >
                Assign All
              </button>
              <button
                type="button"
                disabled={!canEdit || isNoneAssigned}
                title={!canEdit ? "Read only - No edit permission" : undefined}
                onClick={() => handleSelectAllAssigned(false)}
                className={`px-3 py-2 rounded-[6px] font-manrope text-[13px] font-semibold border transition-all shadow-sm ${!canEdit
                  ? "bg-light-100 text-[#9CA3AF] border-light-300 opacity-50 cursor-not-allowed"
                  : isNoneAssigned
                    ? "bg-light-100 text-[#9CA3AF] border-light-300 cursor-not-allowed"
                    : isAllAssigned
                      ? "bg-white text-error border-error/40 hover:bg-error/10 font-bold cursor-pointer"
                      : "bg-white hover:bg-[#E6E8EC] text-secondary hover:text-primary border-light-300 cursor-pointer"
                  }`}
              >
                Unassign All
              </button>
              <Button
                variant="solid"
                className="w-[90px] sm:w-[100px] rounded-[6px] font-manrope text-[13px] font-medium h-[38px] bg-[#BCBEAD] hover:bg-light-600 text-primary"
                onClick={() => setIsManagingPermissions(false)}
              >
                Cancel
              </Button>
              <Button
                variant="validate"
                className={`w-[90px] sm:w-[100px] rounded-[6px] font-manrope text-[13px] font-medium h-[38px] ${!canEdit ? "opacity-50 cursor-not-allowed" : ""
                  }`}
                disabled={!canEdit || submittingPermissions}
                title={!canEdit ? "Read only - No edit permission" : undefined}
                isLoading={submittingPermissions}
                onClick={handleSavePermissions}
              >
                Save
              </Button>
            </div>
          </div>

          {/* 3-Section Split View (25% - 50% - 25%) */}
          <div className="flex flex-col lg:flex-row gap-3.5 mt-3 sm:mt-4 w-full h-[calc(100vh-220px)] min-h-[580px]">
            {/* SECTION 1: 25% Width - Permissions List */}
            <div className="w-full lg:w-[25%] flex flex-col bg-white rounded-[12px] border border-light-300 shadow-sm overflow-hidden flex-shrink-0">
              {/* Section Header */}
              <div className="p-3.5 border-b border-light-300 bg-white">
                <div className="flex items-center justify-between">
                  <p className="font-manrope font-bold text-[15px] text-primary">
                    Permissions
                  </p>
                  <span className="text-[11px] font-manrope font-semibold px-2 py-0.5 rounded-full bg-[#EBF2CC] text-primary border border-active/40">
                    {assignedPermissions.length}/{rolePermissions.length}
                  </span>
                </div>
                {/* Search Bar */}
                <div className="relative mt-2.5">
                  <SearchIcon className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-secondary pointer-events-none" />
                  <input
                    type="text"
                    value={permissionFilterQuery}
                    onChange={(e) => setPermissionFilterQuery(e.target.value)}
                    placeholder="Search permissions..."
                    className="w-full pl-8 pr-7 py-1.5 text-[13px] font-manrope rounded-[6px] border border-light-300 focus:border-primary focus:outline-none bg-gray/40 focus:bg-white transition-all text-primary placeholder-secondary"
                  />
                  {permissionFilterQuery && (
                    <button
                      type="button"
                      onClick={() => setPermissionFilterQuery("")}
                      className="absolute right-2.5 top-1/2 -translate-y-1/2 text-secondary hover:text-primary"
                    >
                      <CloseIcon className="w-3 h-3" />
                    </button>
                  )}
                </div>
              </div>

              {/* Section List Body */}
              <div className="flex-1 overflow-y-auto p-2 space-y-1 os-theme-thin scrollbar-thin">
                {loadingPermissions ? (
                  <div className="py-12 text-center text-secondary">
                    <p className="text-[13px] font-manrope font-medium">Loading permissions...</p>
                  </div>
                ) : filteredRolePermissions.length === 0 ? (
                  <div className="py-8 text-center text-secondary">
                    <p className="text-[13px] font-manrope font-medium">No permissions found</p>
                    <p className="text-[11px] text-secondary mt-0.5">Try a different search term</p>
                  </div>
                ) : (
                  filteredRolePermissions.map((item) => {
                    const isSelected = activePermission?.id === item.id;
                    const subCount = getGrantedSubCount(item.subPermissions);
                    return (
                      <div
                        key={item.id}
                        onClick={() => setActivePermissionId(item.id)}
                        className={`group flex items-center justify-between p-2.5 rounded-[8px] border transition-all cursor-pointer select-none ${isSelected
                          ? "bg-[#F4F9D8] border-active shadow-sm"
                          : item.assigned
                            ? "bg-white border-light-300 hover:border-border hover:bg-gray/40"
                            : "bg-white border-transparent hover:border-light-300 hover:bg-gray/30 opacity-75 hover:opacity-100"
                          }`}
                      >
                        <div className="flex items-center gap-2.5 min-w-0 pr-1">
                          <Checkbox
                            checked={item.assigned}
                            disabled={!canEdit}
                            onClick={(e) => {
                              e.stopPropagation();
                              setActivePermissionId(item.id);
                            }}
                            onChange={(e) => {
                              if (!canEdit) return;
                              handleToggleAssigned(item.id, e.target.checked);
                            }}
                            className="flex-shrink-0"
                          />
                          <span
                            className={`text-[13.5px] font-manrope truncate ${isSelected
                              ? "text-primary font-bold"
                              : item.assigned
                                ? "text-primary font-medium"
                                : "text-secondary"
                              }`}
                            title={item.name}
                          >
                            {capitalizeFirstLetter(item.name)}
                          </span>
                        </div>

                        <div className="flex items-center gap-1.5 flex-shrink-0">
                          <span
                            className={`text-[10px] font-manrope px-1.5 py-0.5 rounded font-semibold ${item.assigned
                              ? isSelected
                                ? "bg-primary text-main"
                                : "bg-primary/90 text-white"
                              : "bg-light-300 text-secondary"
                              }`}
                          >
                            {subCount}/9
                          </span>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            {/* SECTION 2: 50% Width - Sub-permissions Configurator */}
            <div className="w-full lg:w-[50%] flex flex-col bg-white rounded-[12px] border border-light-300 shadow-sm overflow-hidden flex-1">
              {loadingPermissions ? (
                <div className="flex-1 flex items-center justify-center text-secondary">
                  <p className="text-[14px] font-manrope font-medium">Loading sub-permissions...</p>
                </div>
              ) : !activePermission ? (
                <div className="flex-1 flex flex-col items-center justify-center p-6 text-center text-secondary">
                  <div className="w-12 h-12 rounded-full bg-gray flex items-center justify-center mb-2.5 border border-light-300">
                    <SettingsIcon className="w-6 h-6 text-secondary" />
                  </div>
                  <p className="text-[15px] font-manrope font-semibold text-primary">No permission selected</p>
                  <p className="text-[13px] text-secondary mt-1">Select a permission from the list on the left to configure.</p>
                </div>
              ) : (
                <>
                  {/* Active Permission Header */}
                  <div className="p-3.5 sm:p-4 border-b border-light-300 bg-white flex flex-col gap-2.5">
                    {/* Top Row: Title, ID, Status Badge & Assign Checkbox */}
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-center gap-2.5 flex-wrap min-w-0">
                        <span className="text-[12px] font-manrope font-bold px-2 py-0.5 rounded bg-gray text-secondary border border-light-300 flex-shrink-0">
                          #{activePermission.id}
                        </span>
                        <h3 className="text-[17px] sm:text-[19px] font-manrope font-bold text-primary leading-tight">
                          {capitalizeFirstLetter(activePermission.name)}
                        </h3>
                      </div>

                      <div className="flex items-center flex-shrink-0">
                        <Checkbox
                          checked={activePermission.assigned}
                          disabled={!canEdit}
                          onChange={(e) => {
                            if (!canEdit) return;
                            handleToggleAssigned(activePermission.id, e.target.checked);
                          }}
                          className="font-manrope text-[13.5px] text-primary"
                        >
                          <span className="font-manrope font-semibold text-[13.5px] text-primary whitespace-nowrap">
                            {activePermission.assigned ? "Assigned" : "Assign Permission"}
                          </span>
                        </Checkbox>
                      </div>
                    </div>

                    {/* Description if present */}
                    {activePermission.description && (
                      <p className="text-[12.5px] font-manrope text-secondary leading-normal">
                        {activePermission.description}
                      </p>
                    )}

                    {/* Actions Toolbar Row: Sub-actions Status & Bulk Buttons */}
                    <div className="flex flex-wrap items-center justify-between gap-2 pt-1 border-t border-light-200/80 mb-[-11px]">
                      <div className="flex items-center gap-1.5 text-[12px] font-manrope text-secondary">
                        <span className="font-semibold text-primary">
                          {getGrantedSubCount(activePermission.subPermissions)} of 9
                        </span>{" "}
                        sub-permissions enabled
                      </div>

                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          disabled={!canEdit || isAllSubChecked}
                          title={!canEdit ? "Read only - No edit permission" : undefined}
                          onClick={() => handleSelectAllSubForPermission(activePermission.id, true)}
                          className={`text-[12px] font-manrope font-semibold px-2.5 py-1 rounded-[6px] border transition-all shadow-sm ${!canEdit
                            ? "bg-light-100 text-[#9CA3AF] border-light-300 opacity-50 cursor-not-allowed"
                            : isAllSubChecked
                              ? "bg-light-100 text-[#9CA3AF] border-light-300 cursor-not-allowed"
                              : isNoneSubChecked
                                ? "bg-white text-primary border-border hover:bg-[#E6E8EC] font-bold cursor-pointer"
                                : "bg-white hover:bg-[#E6E8EC] text-primary border-light-300 cursor-pointer"
                            }`}
                        >
                          Check All Sub
                        </button>
                        <button
                          type="button"
                          disabled={!canEdit || isNoneSubChecked}
                          title={!canEdit ? "Read only - No edit permission" : undefined}
                          onClick={() => handleSelectAllSubForPermission(activePermission.id, false)}
                          className={`text-[12px] font-manrope font-semibold px-2.5 py-1 rounded-[6px] border transition-all shadow-sm ${!canEdit
                            ? "bg-light-100 text-[#9CA3AF] border-light-300 opacity-50 cursor-not-allowed"
                            : isNoneSubChecked
                              ? "bg-light-100 text-[#9CA3AF] border-light-300 cursor-not-allowed"
                              : isAllSubChecked
                                ? "bg-white text-error border-error/40 hover:bg-error/10 font-bold cursor-pointer"
                                : "bg-white hover:bg-[#E6E8EC] text-secondary hover:text-primary border-light-300 cursor-pointer"
                            }`}
                        >
                          Clear Sub
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Active Permission Sub-permissions Grid */}
                  <div className="flex-1 overflow-y-auto p-4 os-theme-thin scrollbar-thin">
                    {!activePermission.assigned && (
                      <div className="mb-4 p-3 rounded-[8px] bg-gray/80 border border-light-300 flex items-center justify-between text-[13px] font-manrope text-secondary">
                        <p>
                          This permission module is currently <span className="font-semibold text-primary">Unassigned</span>. Ticking any sub-permission below will automatically assign this permission.
                        </p>
                        {canEdit && (
                          <button
                            type="button"
                            onClick={() => handleToggleAssigned(activePermission.id, true)}
                            className="text-[12px] font-bold text-primary underline ml-3 whitespace-nowrap hover:text-secondary"
                          >
                            Assign Module
                          </button>
                        )}
                      </div>
                    )}

                    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-2.5">
                      {subPermissionKeys.map((sub) => {
                        const isChecked = Boolean(activePermission.subPermissions?.[sub.key]);
                        const isListDisabled = sub.key === "can_list";
                        const isCardDisabled = !canEdit || isListDisabled;

                        return (
                          <div
                            key={sub.key}
                            onClick={() => {
                              if (!isCardDisabled) {
                                handleToggleSubPermission(activePermission.id, sub.key, !isChecked);
                              }
                            }}
                            className={`relative p-2.5 sm:p-3 rounded-[8px] border transition-all flex flex-col justify-center select-none ${isCardDisabled
                              ? "cursor-not-allowed bg-[#FAFBF5] border-light-300"
                              : "cursor-pointer"
                              } ${isChecked
                                ? "border-primary/40 bg-[#F7F9EC] shadow-sm hover:border-primary"
                                : "border-light-300 bg-white hover:border-border hover:bg-gray/30"
                              }`}
                            title={
                              isListDisabled
                                ? "List permission is automatically enabled and required when permission is assigned"
                                : !canEdit
                                  ? "Read only - No edit permission"
                                  : undefined
                            }
                          >
                            <div className="flex items-center justify-between gap-2">
                              <div className="flex items-center gap-2 min-w-0">
                                <Checkbox
                                  checked={isChecked}
                                  disabled={isCardDisabled}
                                  className={isListDisabled ? "list-sub-checkbox" : undefined}
                                  onClick={(e) => e.stopPropagation()}
                                  onChange={(e) => {
                                    if (!canEdit) return;
                                    handleToggleSubPermission(activePermission.id, sub.key, e.target.checked);
                                  }}
                                />
                                <span className="text-[13.5px] font-manrope font-semibold text-primary truncate">
                                  {sub.label}
                                </span>
                              </div>

                              {isListDisabled && (
                                <span className="text-[10px] font-manrope font-semibold px-1.5 py-0.5 rounded bg-white text-secondary border border-light-300 flex-shrink-0">
                                  Required
                                </span>
                              )}
                            </div>

                            <p className="text-[11.5px] font-manrope text-secondary mt-1 leading-normal">
                              {sub.desc}
                            </p>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* SECTION 3: 25% Width - Selected Permissions Real-time Summary */}
            <div className="w-full lg:w-[25%] flex flex-col bg-white rounded-[12px] border border-light-300 shadow-sm overflow-hidden flex-shrink-0">
              {/* Summary Header */}
              <div className="p-3.5 border-b border-light-300 bg-white">
                <div className="flex items-center justify-between">
                  <p className="font-manrope font-bold text-[15px] text-primary">
                    Selected Summary
                  </p>
                </div>

                {/* Summary Mini Stats Bar */}
                <div className="mt-2.5 p-2 rounded-[8px] bg-gray border border-light-300 flex items-center justify-between text-[12px] font-manrope">
                  <div>
                    <p className="text-secondary text-[11px]">Modules</p>
                    <p className="text-primary font-bold text-[13px]">{assignedPermissions.length} assigned</p>
                  </div>
                  <div className="text-right">
                    <p className="text-secondary text-[11px]">Sub-actions</p>
                    <p className="text-primary font-bold text-[13px]">{totalGrantedSubCount} granted</p>
                  </div>
                </div>
              </div>

              {/* Summary Items List */}
              <div className="flex-1 overflow-y-auto p-2.5 space-y-2 os-theme-thin scrollbar-thin">
                {assignedPermissions.length === 0 ? (
                  <div className="h-full flex flex-col items-center justify-center p-4 text-center text-secondary my-auto">
                    <div className="w-10 h-10 rounded-full bg-gray flex items-center justify-center mb-2 border border-light-300">
                      <SettingsIcon className="w-5 h-5 text-secondary" />
                    </div>
                    <p className="font-manrope font-semibold text-[13px] text-primary">No assigned permissions</p>
                    <p className="font-manrope text-[11px] text-secondary mt-1 max-w-[200px]">
                      Check permissions from the left list or configure sub-permissions in the center.
                    </p>
                  </div>
                ) : (
                  assignedPermissions.map((assignedItem) => {
                    const isSelected = activePermission?.id === assignedItem.id;
                    const activeSubs = subPermissionKeys.filter(
                      (sub) => assignedItem.subPermissions?.[sub.key]
                    );

                    return (
                      <div
                        key={assignedItem.id}
                        className={`p-2.5 rounded-[8px] border transition-all ${isSelected
                          ? "border-active bg-[#F4F9D8] shadow-sm"
                          : "border-light-300 bg-white hover:border-border hover:bg-gray/30"
                          }`}
                      >
                        <div className="flex items-center justify-between gap-1 mb-1.5">
                          <button
                            type="button"
                            onClick={() => setActivePermissionId(assignedItem.id)}
                            className="text-left font-manrope font-bold text-[13px] text-primary truncate hover:underline flex-1"
                            title={`Click to view ${assignedItem.name}`}
                          >
                            {capitalizeFirstLetter(assignedItem.name)}
                          </button>
                          {canEdit && (
                            <button
                              type="button"
                              onClick={() => handleToggleAssigned(assignedItem.id, false)}
                              className="w-5 h-5 flex items-center justify-center text-secondary hover:text-error rounded hover:bg-white transition-colors flex-shrink-0"
                              title="Unassign permission"
                            >
                              <DeleteIcon className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>

                        {/* Granted Sub-permission Mini Tags */}
                        <div className="flex flex-wrap gap-1">
                          {activeSubs.map((sub) => (
                            <span
                              key={sub.key}
                              className="text-[10px] font-manrope px-1.5 py-0.5 rounded bg-white text-primary border border-light-300 font-medium"
                            >
                              {sub.label}
                            </span>
                          ))}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="w-full h-full flex-col p-2 sm:p-4">
          {/* Header */}
          <div className="w-full flex flex-row items-center justify-between">
            <p className="text-[25px] sm:text-[32px] font-manrope text-primary font-medium leading-[37.5px] sm:leading-[48px]">
              Manage Roles Permission
            </p>
            <PermissionGate
              module={[permissionConstants.ROLES_PERMISSIONS]}
              action="can_add"
            >
              <div className="flex flex-row items-center gap-3">
                <button
                  className="flex flex-row items-center justify-between px-2 sm:px-4 py-2 gap-2 bg-white rounded-[6px] flex-shrink-0 hover:bg-[#E6E8EC]"
                  onClick={handleOpenCreateRole}
                >
                  <p className="font-manrope text-[14px] font-medium text-primary">
                    Create Role
                  </p>
                  <PlusIcon className="w-[18px] h-[18px] text-primary" />
                </button>
              </div>
            </PermissionGate>
          </div>

          {/* Search & Actions Toolbar */}
          <div className="mt-2 sm:mt-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
            <SearchFilter_1
              options={[{ code: "name", name: "Role" }]}
              placeholder="Search by role name"
              value="name"
              onChange={() => { }}
              searchText={searchQuery}
              setSearchText={setSearchQuery}
            />

            <div className="flex items-center gap-2">
              <button
                className="flex flex-row items-center justify-between px-3 py-2 gap-2 bg-white rounded-[6px] flex-shrink-0 hover:bg-[#E6E8EC] h-[44px] border border-light-300"
                onClick={() => fetchRoles(currentPage, pageSize, debouncedSearch)}
              >
                <p className="font-manrope text-[14px] font-medium text-primary">
                  Refresh
                </p>
                <ResetIcon className="w-[16px] h-[16px] text-primary" />
              </button>
            </div>
          </div>

          {/* Roles Table */}
          {!canViewRoles ? (
            <div className="flex flex-col items-center justify-center bg-white rounded-[12px] p-8 mt-2 sm:mt-4 text-center shadow-sm">
              <p className="font-manrope text-[16px] font-semibold text-secondary">
                You do not have permission to view roles and permissions.
              </p>
            </div>
          ) : (
            <div className="flex flex-col w-full bg-white rounded-[12px] p-4 mt-2 sm:mt-4">
              <Table
                loading={loadingRoles}
                showSorterTooltip={false}
                columns={columns}
                dataSource={roles}
                pagination={{
                  total: totalRoles,
                  current: currentPage,
                  pageSize: pageSize,
                  onChange: onPageChange,
                  position: ["bottomCenter"],
                  defaultPageSize: LIST_PAGE_SIZE,
                  showSizeChanger: false,
                }}
                rowKey="id"
              />
            </div>
          )}
        </div>
      )}

      {/* Create / Edit Role Modal */}
      <Modal
        open={isRoleModalOpen}
        onCancel={() => {
          setIsRoleModalOpen(false);
          setNewRoleName("");
          setSelectedChannelPartnerId(null);
          setEditingRole(null);
        }}
        footer={null}
        title={
          <p className="text-[20px] font-manrope font-medium text-primary">
            {editingRole ? "Edit Role" : "Create New Role"}
          </p>
        }
      >
        <form onSubmit={handleSaveRoleSubmit} className="mt-4 flex flex-col gap-3 font-manrope">
          {!editingRole && (
            <div>
              <p className="text-[14px] font-manrope font-medium text-secondary mb-1.5">
                Channel Partner <span className="text-error">*</span>
              </p>
              <Select
                size="large"
                placeholder="Select channel partner"
                loading={loadingChannelPartners}
                value={selectedChannelPartnerId || undefined}
                onChange={(val) => setSelectedChannelPartnerId(val)}
                className="w-full font-manrope custom-select"
                popupClassName="custom-select-dropdown"
                options={channelPartners.map((cp) => ({
                  value: cp.id,
                  label: cp.name,
                }))}
                showSearch
                filterOption={(input, option) =>
                  (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                }
                notFoundContent={
                  loadingChannelPartners ? "Loading channel partners..." : "No active channel partners found"
                }
              />
            </div>
          )}
          <div>
            <p className="text-[14px] font-manrope font-medium text-secondary mb-1.5">
              Role Name <span className="text-error">*</span>
            </p>
            <input
              type="text"
              className="h-[48px] placeholder-other w-full appearance-none text-[15px] font-manrope font-medium rounded-[10px] border border-border bg-white px-4 py-2 text-primary focus:border-active focus:ring-0 focus:outline-none"
              placeholder="Enter role name (e.g. Manager, Sales, Support Staff)"
              value={newRoleName}
              onChange={(e) => setNewRoleName(e.target.value)}
            />
          </div>

          <div className="w-full flex flex-row items-center justify-end gap-2 mt-4 pt-4 border-t border-light-300">
            <Button
              variant="solid"
              type="button"
              className="w-[120px] rounded-[8px] font-manrope text-[14px] font-bold leading-[20px] h-[44px] bg-[#BCBEAD] hover:bg-light-600"
              onClick={() => {
                setIsRoleModalOpen(false);
                setNewRoleName("");
                setSelectedChannelPartnerId(null);
                setEditingRole(null);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="validate"
              type="submit"
              className="w-[120px] rounded-[8px] font-manrope text-[14px] font-bold leading-[20px] h-[44px]"
              disabled={createRoleLoading}
              isLoading={createRoleLoading}
            >
              {editingRole ? "Update" : "Create"}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Duplicate Role Modal */}
      <Modal
        open={isDuplicateModalOpen}
        onCancel={() => {
          setIsDuplicateModalOpen(false);
          setDuplicateRoleName("");
          setDuplicateChannelPartnerId(null);
          setDuplicatingRole(null);
        }}
        footer={null}
        title={
          <p className="text-[20px] font-manrope font-medium text-primary">
            Duplicate Role
          </p>
        }
      >
        <form onSubmit={handleDuplicateRoleSubmit} className="mt-4 flex flex-col gap-3 font-manrope">
          <div>
            <p className="text-[14px] font-manrope font-medium text-secondary mb-1.5">
              Channel Partner <span className="text-error">*</span>
            </p>
            <Select
              size="large"
              placeholder="Select channel partner"
              loading={loadingChannelPartners}
              value={duplicateChannelPartnerId || undefined}
              onChange={(val) => setDuplicateChannelPartnerId(val)}
              className="w-full font-manrope custom-select"
              popupClassName="custom-select-dropdown"
              options={channelPartners.map((cp) => ({
                value: cp.id,
                label: cp.name,
              }))}
              showSearch
              filterOption={(input, option) =>
                (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
              }
              notFoundContent={
                loadingChannelPartners ? "Loading channel partners..." : "No active channel partners found"
              }
            />
          </div>

          <div>
            <p className="text-[14px] font-manrope font-medium text-secondary mb-1.5">
              Role Name <span className="text-error">*</span>
            </p>
            <input
              type="text"
              className="h-[48px] placeholder-other w-full appearance-none text-[15px] font-manrope font-medium rounded-[10px] border border-border bg-white px-4 py-2 text-primary focus:border-active focus:ring-0 focus:outline-none"
              placeholder="Enter new role name"
              value={duplicateRoleName}
              onChange={(e) => setDuplicateRoleName(e.target.value)}
            />
          </div>

          <div className="w-full flex flex-row items-center justify-end gap-2 mt-4 pt-4 border-t border-light-300">
            <Button
              variant="solid"
              type="button"
              className="w-[120px] rounded-[8px] font-manrope text-[14px] font-bold leading-[20px] h-[44px] bg-[#BCBEAD] hover:bg-light-600"
              onClick={() => {
                setIsDuplicateModalOpen(false);
                setDuplicateRoleName("");
                setDuplicateChannelPartnerId(null);
                setDuplicatingRole(null);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="validate"
              type="submit"
              className="w-[120px] rounded-[8px] font-manrope text-[14px] font-bold leading-[20px] h-[44px]"
              disabled={duplicateLoading}
              isLoading={duplicateLoading}
            >
              Duplicate
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default ManageRolesPermissionPage;
